import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, KeyRound, ArrowRight } from "lucide-react";

const Login = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const from = location.state?.from?.pathname;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email.trim(), password);
            toast.success(`Welcome back, ${u.name || u.email}`);
            navigate(from || (u.role === "admin" ? "/admin" : "/dashboard"), { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-72px)] bg-white" data-testid="login-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-6">
                    <div className="un-label text-un-grape mb-6">Client Sign In</div>
                    <h1 className="un-h1 text-3xl md:text-4xl lg:text-5xl mb-6 leading-[1.1]">
                        Welcome Back To Your <br className="hidden md:inline" />
                        <span className="text-un-berry">UnCloned® Short Form Video Strategist.</span>
                    </h1>
                    <p className="text-lg text-un-onyx/75 max-w-lg mb-10 leading-snug">
                        Sign in to keep creating authority-building short-form video scripts.
                        Your saved projects and script versions will be right where you left them.
                    </p>

                    <div className="un-card p-6 max-w-lg" data-testid="new-here-card">
                        <div className="flex items-start gap-4">
                            <div className="hidden sm:flex bg-un-berry text-white w-12 h-12 items-center justify-center border-2 border-un-onyx flex-shrink-0">
                                <KeyRound className="w-5 h-5" />
                            </div>
                            <div className="flex-1">
                                <div className="un-label text-un-grape mb-2">New Here?</div>
                                <p className="text-un-onyx/75 text-sm mb-4">
                                    Redeem the activation code UnCloned® sent you to unlock your private account.
                                </p>
                                <Link
                                    to="/signup"
                                    className="un-btn-outline text-sm inline-flex items-center gap-2"
                                    data-testid="link-to-signup"
                                >
                                    Redeem Activation Code <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-6">
                    <form
                        onSubmit={submit}
                        className="un-card p-8 md:p-10 max-w-lg lg:ml-auto"
                        data-testid="login-form"
                    >
                        <div className="un-label text-un-grape mb-6">Sign In To Your Account</div>
                        <label className="block mb-5">
                            <span className="un-label text-un-onyx block mb-2">Email</span>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                className="un-input"
                                placeholder="you@brand.com"
                                data-testid="login-email-input"
                            />
                        </label>
                        <label className="block mb-8">
                            <span className="un-label text-un-onyx block mb-2">Password</span>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                className="un-input"
                                placeholder="••••••••"
                                data-testid="login-password-input"
                            />
                        </label>
                        <button
                            type="submit"
                            disabled={loading}
                            className="un-btn-primary w-full text-base flex items-center justify-center gap-2"
                            data-testid="login-submit-button"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {loading ? "Signing In…" : "Sign In"}
                        </button>
                        <div className="un-divider mt-8 pt-5 flex items-center justify-between text-sm">
                            <span className="text-un-onyx/60">Have an activation code?</span>
                            <Link to="/signup" className="un-btn-ghost !px-0" data-testid="login-to-signup">
                                Redeem →
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;
